
import { computed, defineComponent, onBeforeMount, onBeforeUnmount, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

// Components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// Validation
import useVuelidate from '@vuelidate/core'
import { required, integer } from '@vuelidate/validators'

// Composable
import useNotifications from '@/common/composable/useNotifications'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { CarrierT } from '@/core/types/carriers.types'
import { CourierCarPayloadT, VehicleType } from '@/core/types/couriers.types'
import { FTLCarTypeT } from '@/core/types/ftl.types'
import {
    AutoDataResponseT,
    InProgressDataResponseT,
    InvalidDataResponseT,
    SecurityGetAutoInformationResponseT,
    SecurityGetCheckupStatusResponseT,
} from '@/core/types/security.types'

// Realisations
import { carriers } from '@/core/realisations/carriers.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'
import { FTL } from '@/core/realisations/FTL.realisation'
import { security } from '@/core/realisations/security.realisation'

const AUTO_CHECKUP_TYPE = 3
const TIMER_INTERVAL = 7 * 1000 // 7s

export default defineComponent({
    components: {
        PageTitle,
        TheInput,
        TheButton,
        TheSelect,
        FileReader,
    },
    setup() {
        const router = useRouter()
        const notifications = useNotifications()

        const is_loading = ref(false)
        const step = ref(1)

        const state = reactive({
            number: null as string | null,
            company_id: null as number | null,
            vehicle_type_id: null as number | null,
            model: null as string | null,
            comment: null as string | null,
            ftl_car_type_id: null as number | null,
            cubature: null as string | null,
            code_1C: null as string | null,
            technicalPassport: [] as File[],
        })

        const state_form$ = useVuelidate(
            {
                number: { required },
                company_id: { required },
                vehicle_type_id: { required },
                model: { required },
                ftl_car_type_id: { required },
                cubature: { required, integer },
            },
            state
        )

        const sb_state = reactive({
            is_checked: false,
            is_rejected: false,
            is_loading: false,
            reject_comment: null as string | null,
            is_request_completed: false,
        })

        const resetSbState = () => {
            sb_state.is_checked = false
            sb_state.is_rejected = false
            sb_state.reject_comment = null
            sb_state.is_request_completed = false
        }

        const auto_data = reactive({
            is_loading: false,
            is_information_loading: false,
            is_not_found: false,
            is_in_progress: false,
            not_found_message: null as string | null,
            update_timer: null as number | null,
            is_loaded: false,
        })

        const createAutoUpdateTimer = () => {
            // @ts-ignore
            auto_data.update_timer = setInterval(getAutoInformation, TIMER_INTERVAL)
        }

        const removeAutoUpdateTimer = () => {
            // @ts-ignore
            clearInterval(auto_data.update_timer)
            auto_data.update_timer = null
        }

        // const isAutoNotFound = (response: SecurityGetAutoInformationResponseT): boolean => {
        //     const invalid_response = response as InvalidDataResponseT

        //     const key = invalid_response.key
        //     const message = invalid_response.message

        //     if (key && key === 'not_found') {
        //         auto_data.not_found_message = message
        //         return true
        //     }

        //     auto_data.not_found_message = null
        //     return false
        // }

        // const isAutoInProgress = (response: SecurityGetAutoInformationResponseT): boolean => {
        //     const key = (response as InProgressDataResponseT).key

        //     if (key && key === 'in_progress') return true
        //     return false
        // }

        const getAutoInformation = async () => {
            auto_data.is_loaded = false

            try {
                auto_data.is_information_loading = true

                // const auto_number = state.number as string
                // const response = await security.getAutoInformation(auto_number)

                // auto_data.is_not_found = isAutoNotFound(response)
                // auto_data.is_in_progress = isAutoInProgress(response)

                if (auto_data.is_not_found) {
                    removeAutoUpdateTimer()
                    return
                }

                if (auto_data.is_in_progress) {
                    if (!auto_data.update_timer) {
                        createAutoUpdateTimer()
                    }
                    return
                }

                removeAutoUpdateTimer()

                loadDataState()

                step.value = 2
                auto_data.is_loaded = true
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении информации о авто произошла ошибка',
                })
            } finally {
                auto_data.is_information_loading = false
            }
        }

        // const createAutoInformationRequest = async () => {
        //     try {
        //         auto_data.is_loading = true

        //         const auto_number = state.number as string
        //         const technical_passport = ''

        //         await security.createAutoInformationRequest(auto_number, technical_passport)

        //         getAutoInformation()
        //     } catch (error) {
        //         notifications.notifyError({
        //             error,
        //             title: 'При создании запроса на получение информации о авто произошла ошибка',
        //         })
        //     } finally {
        //         auto_data.is_loading = false
        //     }
        // }

        const checkSbStatus = async () => {
            if (sb_state.is_loading || auto_data.is_loading || auto_data.is_information_loading)
                return
            resetSbState()

            try {
                sb_state.is_loading = true

                const response: SecurityGetCheckupStatusResponseT = await security.getCheckupStatus(
                    {
                        identifier: state.number as string,
                        type: AUTO_CHECKUP_TYPE,
                    }
                )

                sb_state.is_checked = true

                if (!response.data.isAccepted) {
                    sb_state.is_rejected = true
                    sb_state.reject_comment = response.data.comment

                    notifications.notify({
                        title: response.message || 'При проверке статуса СБ произошла ошибка',
                        type: 'danger',
                    })
                } else {
                    getAutoInformation()
                    // createAutoInformationRequest()
                }
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })

                sb_state.is_checked = false
            } finally {
                sb_state.is_loading = false
                sb_state.is_request_completed = true
            }
        }

        const submitForm = async () => {
            if (step.value === 1) {
                checkSbStatus()
                return
            }

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                is_loading.value = true

                const form_data = new FormData()

                let company_id = state.company_id as number
                let vehicle_type_id = state.vehicle_type_id as number
                let ftl_car_type_id = state.ftl_car_type_id as number

                form_data.append('number', state.number as string)
                form_data.append('company_id', company_id.toString())
                form_data.append('vehicle_type_id', vehicle_type_id.toString())
                form_data.append('model', state.model as string)
                form_data.append('comment', state.comment || '')
                form_data.append('ftl_car_type_id', ftl_car_type_id.toString())
                form_data.append('cubature', state.cubature as string)
                form_data.append('code_1C', state.code_1C || '')

                if (state.technicalPassport.length) {
                    state.technicalPassport.forEach((technicalPassport) => {
                        form_data.append('technical_passports[]', technicalPassport)
                    })
                }

                await couriers.createCar(form_data)

                notifications.notify({ title: 'Машина добавлена', type: 'success' })
                router.push('/couriers/cars')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При добавлении машины произошла ошибка',
                })
            } finally {
                is_loading.value = false
            }
        }

        const data_state = reactive({
            carriers: new DataState<CarrierT>({}),
            vehicle_types: new DataState<VehicleType>({}),
            ftl_car_types: new DataState<FTLCarTypeT>({}),
        })

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    search,
                    getAllCarriers: 1,
                    limit: 50,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true
                const response = await carriers.getList(params)
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    getAllCarriers: 1,
                    limit: 50,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true

                const response = await carriers.getList(params)
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        const loadVehicleTypes = async (page: number) => {
            data_state.vehicle_types.pagination.current_page = page

            try {
                data_state.vehicle_types.is_loading = true
                const response = await couriers.getVehicleTypes(page)
                data_state.vehicle_types.list = response.data

                data_state.vehicle_types.pagination.total_pages = response.meta.last_page

                data_state.vehicle_types.is_failed = false
                data_state.vehicle_types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.vehicle_types.failure.catchError(error)

                if (!data_state.vehicle_types.failure.error_state.is_cancel) {
                    data_state.vehicle_types.is_failed = true
                }
            } finally {
                if (!data_state.vehicle_types.failure.error_state.is_cancel) {
                    data_state.vehicle_types.is_loading = false
                }
            }
        }

        const loadMoreVehicleTypes = async (page: number) => {
            if (page > data_state.vehicle_types.pagination.total_pages) return
            data_state.vehicle_types.pagination.current_page = page

            try {
                data_state.vehicle_types.is_loading = true

                const response = await couriers.getVehicleTypes(page)
                data_state.vehicle_types.list = data_state.vehicle_types.list.concat(response.data)

                data_state.vehicle_types.pagination.total_pages = response.meta.last_page

                data_state.vehicle_types.is_failed = false
                data_state.vehicle_types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.vehicle_types.failure.catchError(error)

                if (!data_state.vehicle_types.failure.error_state.is_cancel) {
                    data_state.vehicle_types.is_failed = true
                }
            } finally {
                if (!data_state.vehicle_types.failure.error_state.is_cancel) {
                    data_state.vehicle_types.is_loading = false
                }
            }
        }

        const loadFtlCarTypes = async () => {
            data_state.ftl_car_types.pagination.current_page = 1

            try {
                data_state.ftl_car_types.is_loading = true
                const response = await FTL.getCarTypes()

                data_state.ftl_car_types.list = response.data
                data_state.ftl_car_types.pagination.total_pages = 1

                data_state.ftl_car_types.is_failed = false
                data_state.ftl_car_types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.ftl_car_types.failure.catchError(error)

                if (!data_state.ftl_car_types.failure.error_state.is_cancel) {
                    data_state.ftl_car_types.is_failed = true
                }
            } finally {
                if (!data_state.ftl_car_types.failure.error_state.is_cancel) {
                    data_state.ftl_car_types.is_loading = false
                }
            }
        }

        const loadDataState = () => {
            loadCarriers(1)
            loadVehicleTypes(1)
            loadFtlCarTypes()
        }

        const isRejected = computed(
            () => step.value === 1 && sb_state.is_rejected && sb_state.is_request_completed
        )

        const isNotChecked = computed(
            () => step.value === 1 && !sb_state.is_checked && sb_state.is_request_completed
        )

        onBeforeUnmount(removeAutoUpdateTimer)

        return {
            auto_data,
            step,
            resetSbState,
            sb_state,
            state,
            state_form$,
            is_loading,
            submitForm,
            //
            data_state,
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            loadVehicleTypes,
            loadMoreVehicleTypes,
            isRejected,
            isNotChecked,
        }
    },
})
